import {Component, OnInit} from '@angular/core';
import {ApiResponse, BaseModalComponent, LeToastrService, PRIVILEGES} from 'common-shared';
import {BehaviorSubject, debounceTime, Observable, Subject} from 'rxjs';
import {filter, takeUntil, tap} from 'rxjs/operators';
import {marker} from "@jsverse/transloco-keys-manager/marker";
import {ResourceModel} from "../models/resource.model";
import {ResourceService} from "../resource.service";

@Component({
  selector: 'mes-web-resource-edit-modal',
  templateUrl: './resource-edit-modal.component.html'
})
export class ResourceEditModalComponent extends BaseModalComponent implements OnInit {
  entityId: string;
  actionType: ActionType;
  entityName: string;
  resource: ResourceModel;
  resource$: Observable<ResourceModel>;
  response: ApiResponse<any>;
  isPristine$ = new BehaviorSubject<boolean>(true);
  wasValidated$ = new Subject<boolean>();
  protected readonly PRIVILEGES = PRIVILEGES;
  listenCacheUpdate = false;

  constructor(private resourceService: ResourceService, private toastrService: LeToastrService) {
    super();
  }

  ngOnInit() {
    if (this.entityId) {
      this.resource$ = this.resourceService.loadOne(this.entityId);
    } else {
      this.resource$ = new Observable<ResourceModel>(subscriber => {
        subscriber.next(ResourceModel.CreateDefault({Code: this.entityName, Name: this.entityName}));
      });
    }
    this.resource$ = this.resource$.pipe(tap(m => {
      this.resource = m;
      this.notifyIfEntityIsUpdatedElsewhere();
    }));
  }

  onChanged(isPristine: boolean) {
    this.isPristine$.next(isPristine);
  }

  save(withClose: boolean = true) {
    this.wasValidated$.next(true);
    if (this.resource.isValid()) {
      this.listenCacheUpdate = false;
      this.resourceService.save(this.resource).pipe(takeUntil(this.componentDestroyed)).subscribe({
        next: value => {
          this.entityId = value.Id;
          this.actionType = 'saved';
          if (withClose) {
            this.close();
          } else {
            this.resource = value;
          }
        }, error: value1 => {
          this.response = value1.error;
        }
      });
    }
  }

  delete() {
    this.resourceService.remove(this.resource).pipe(takeUntil(this.componentDestroyed)).subscribe({
      next: value => {
        this.entityId = null;
        this.actionType = 'deleted';
        this.close();
      }, error: value1 => {
        this.response = value1.error;
      }
    });
  }

  add() {
    this.wasValidated$.next(true);
    if (this.resource.isValid()) {
      this.resourceService.addNew(this.resource).pipe(takeUntil(this.componentDestroyed)).subscribe({
        next: value => {
          this.entityId = value.Id;
          this.actionType = 'added';
          this.close();
        }, error: value1 => {
          this.response = value1.error;
        }
      });
    }
  }

  private notifyIfEntityIsUpdatedElsewhere() {
    this.resourceService.query.selectEntity(this.resource.Id).pipe(takeUntil(this.componentDestroyed), debounceTime(1000), filter(resource => {
      if (!resource) return false;
      const listenCacheUpdate = this.listenCacheUpdate;
      if (!listenCacheUpdate) {
        this.listenCacheUpdate = true;
      }
      return listenCacheUpdate;
    })).subscribe(item => {
      this.toastrService.translatedWarning(marker('ime.manufacturing.resources.Resource has been updated elsewhere. Reload page'), marker('ime.manufacturing.resources.Reload'));
    });
  }
}

export type ActionType = 'saved' | 'deleted' | 'added';
